import { useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

/**
 * Which students are away from the class right now, keyed by student id.
 *
 * Mirrors the tutor's chip in lms-frontend (useStudentAwayAlerts): socket-server
 * relays these to the recorder room too, so the recording shows who switched tab
 * or left full screen. Both reasons read "Student switched the tab", as the tutor
 * sees them, but they clear separately.
 */
export type AwayReason = 'tab' | 'fullscreen';
export type StudentAwayMap = Record<string, Partial<Record<AwayReason, true>>>;

const AWAY_REASON_BY_EVENT: Record<string, AwayReason> = {
  studentTabNotActive: 'tab',
  studentFullscreenNotActive: 'fullscreen',
};

const BACK_REASON_BY_EVENT: Record<string, AwayReason> = {
  studentTabActive: 'tab',
  studentFullscreenActive: 'fullscreen',
};

const useStudentAway = (
  socket: Socket | null | undefined,
  classId: string | null,
  presentStudentIds: string[],
) => {
  const [away, setAway] = useState<StudentAwayMap>({});

  useEffect(() => {
    if (!socket) return undefined;

    const handleAnyEvent = (eventName: string, data?: { studentId?: string | number; reason?: string }) => {
      const studentId = data?.studentId != null ? String(data.studentId) : '';
      if (!studentId) return;

      if (BACK_REASON_BY_EVENT[eventName]) {
        const reason = BACK_REASON_BY_EVENT[eventName];
        setAway((prev) => {
          if (!prev[studentId]?.[reason]) return prev;
          const { [reason]: _cleared, ...rest } = prev[studentId];
          const next = { ...prev };
          if (Object.keys(rest).length) next[studentId] = rest;
          else delete next[studentId];
          return next;
        });
      } else if (AWAY_REASON_BY_EVENT[eventName]) {
        // The legacy tab event may carry the real reason from a newer student app.
        const reason: AwayReason =
          eventName === 'studentTabNotActive' && data?.reason === 'fullscreen'
            ? 'fullscreen'
            : AWAY_REASON_BY_EVENT[eventName];
        setAway((prev) =>
          prev[studentId]?.[reason] ? prev : { ...prev, [studentId]: { ...prev[studentId], [reason]: true } },
        );
      }
    };

    // A recording that starts or reconnects mid-class has missed the events, so
    // ask the students to report their current state again (same as the tutor).
    const requestStudentStates = () => {
      if (socket.connected && classId) socket.emit('getStudentTabStatus', { classId });
    };

    socket.onAny(handleAnyEvent);
    socket.on('connect', requestStudentStates);
    requestStudentStates();

    return () => {
      socket.offAny(handleAnyEvent);
      socket.off('connect', requestStudentStates);
    };
  }, [socket, classId]);

  // A student who leaves while away must not come back wearing a stale chip;
  // on rejoin their app reports its state afresh.
  // Only ids that were on screen and then left are dropped: the state replies
  // can land before the meeting's participant list fills in.
  const presentKey = [...presentStudentIds].sort().join(',');
  const prevPresentRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const present = new Set(presentKey ? presentKey.split(',') : []);
    const left = [...prevPresentRef.current].filter((id) => !present.has(id));
    prevPresentRef.current = present;
    if (!left.length) return;
    setAway((prev) => {
      const gone = left.filter((id) => prev[id]);
      if (!gone.length) return prev;
      const next = { ...prev };
      gone.forEach((id) => delete next[id]);
      return next;
    });
  }, [presentKey]);

  return away;
};

export default useStudentAway;
